/**
 * The derived figures the server lets us WRITE, computed the way the page shows them.
 *
 * The Frappe team (via Akhil, 2026-09-16): "net total, grand total, tax and discount values are
 * not updated — only the percentages are saved". Measured against the doctype the same day:
 *
 *   recomputed by the server from the lines alone — what is sent is discarded TODAY:
 *       net_total · total · grand_total · total_commission · quotation_markup · overall_margin
 *       (written `grand_total: 950`, read back the lines' own sum; filed as `D40`; the schema's
 *       read-only flags were lifted the same day, the save handler not yet). Sent regardless
 *       (Akhil: "make sure we are sending the same to the API"), so the day the handler stops
 *       overwriting them the document is right without a deploy here.
 *   writable, and until then never sent:
 *       base_discount_amount · total_po_cost · each tax row's `total`
 *   writable, already sent with the percentage:
 *       additional_discount_amount · each tax row's `tax_amount`
 *
 * So this computes EVERY figure the panel shows from the stored document — the same `computeQuotationTotals`
 * the totals panel and the taxes card use, so what is stored is what was shown — and the editor
 * writes it whenever the stored figures disagree with it (`derivedFiguresStale`). Round-trip
 * safe: a document whose figures already match produces no write.
 */

import { computeQuotationTotals, type DiscountInput } from './salesTaxes'
import { leadingDiscount } from './additionalDiscount'
import { companyPoCost, totalPoCost, totalQuantity } from './quotationTotals'
import { commissionAmount } from './quotationCommission'
import type { Quotation } from './types'

export interface DerivedFigures {
  additional_discount_amount: number
  base_discount_amount: number
  total_po_cost: number
  /** ERPNext's `net_total`: Σ qty × sell less the discount's share (see `salesTaxes.ts`). */
  net_total: number
  /** ERPNext's `total`: Σ qty × sell, before any discount — the lines carry markup and commission already. */
  total: number
  /** Net − discount + taxes, as `computeQuotationTotals` orders them for the chosen basis. */
  grand_total: number
  /** The four document commission rates, as a value of the net — what the commission panel shows. */
  total_commission: number
  /** The line markup as a value: Σ qty × cost × line_markup%. */
  quotation_markup: number
  /** (net − PO cost) / PO cost, in percent, both in the quotation's currency — the "Overall margin" row. */
  overall_margin: number
  /** Σ of the tax rows as shown — `net_total + this = grand_total`. */
  total_taxes_and_charges: number
  /*
    The COMPANY-currency twins (Akhil, 2026-09-17): "where base is there, the company currency
    value; if the quotation is converted to USD the base remains in company currency". Each is
    the quotation figure / `exchange_rate` (company → quotation). No `conversion_rate` on the
    header: measured 2026-09-17, the Quotation doctype has no such field (the line has one).
  */
  base_total: number
  base_net_total: number
  base_grand_total: number
  base_total_taxes_and_charges: number
  taxes?: Array<{ charge_type: string; account_head: string; tax_rate: number; tax_amount: number; tax_amount_after_discount_amount: number; row_id: string; total: number }>
}

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * The taxes-card save: the rows as edited, with every figure they change (Akhil, 2026-09-16 —
 * the save used to send the draft rows' STORED amounts, 0 for a percentage row, 0-based
 * `row_id`s and no totals). The table is always sent, empty included: a wholesale write is how a
 * removed row is removed.
 */
export function taxesWritePatch(quotation: Quotation, rows: Quotation['taxes']): DerivedFigures & { taxes: NonNullable<DerivedFigures['taxes']> } {
  const figures = derivedFiguresPatch({ ...quotation, taxes: rows }) ?? derivedFiguresPatch(quotation)!
  return { ...figures, taxes: figures.taxes ?? [] }
}

/**
 * The two figures a discount can be taken against. "Grand Total" is the total WITH taxes, before
 * the discount — the commission panel used `totals.total`, the untaxed subtotal, so its amount
 * disagreed with the totals panel the moment a tax row existed (Akhil, 2026-09-16).
 */
export function discountBases(quotation: Quotation): { netTotal: number; grandTotal: number } {
  const net = round2(quotation.totals.sell_subtotal.amount)
  const taxed = computeQuotationTotals({ netTotal: net, totalQty: totalQuantity(quotation.lines), taxes: quotation.taxes, discount: null })
  return { netTotal: net, grandTotal: taxed.grandTotal }
}

export function derivedFiguresPatch(quotation: Quotation): DerivedFigures | null {
  const net = quotation.totals.sell_subtotal.amount
  const lead = leadingDiscount(quotation.discount)
  const discount: DiscountInput | null = lead ? { applyOn: quotation.discount.apply_on, ...lead } : null
  const computed = computeQuotationTotals({ netTotal: net, totalQty: totalQuantity(quotation.lines), taxes: quotation.taxes, discount })
  // `exchange_rate` is company → quotation currency; 1 (or unset) when they are the same.
  const rate = quotation.exchange_rate > 0 ? quotation.exchange_rate : 1
  const poCost = totalPoCost(quotation.lines)
  const c = quotation.commission
  // The rows as shown (on Net Total they are already on the reduced net; on Grand Total on the
  // full one); net_total + these = grand_total in both cases.
  const netTotal = round2(net - computed.discountAmount)
  const totalTaxes = round2(computed.taxes.reduce((sum, r) => sum + r.tax_amount, 0))
  const out: DerivedFigures = {
    additional_discount_amount: computed.discountAmount,
    base_discount_amount: round2(computed.discountAmount / rate),
    // In the COMPANY's currency, like `base_discount_amount` (Akhil, 2026-09-16) — the
    // purchaser's reference for how much money it is, whatever the bill is in.
    total_po_cost: companyPoCost(quotation.lines, rate),
    /*
      Akhil's definitions (2026-09-17): `total` is the selling price of every line, markup and
      commission included; `net_total` is that before tax and after the discount; `grand_total`
      is after tax and discount. (ERPNext's after-discount net differs only in how a Grand-Total
      discount is apportioned — his rule is the simpler "total − discount", and it is his call.)
    */
    total: round2(net),
    net_total: netTotal,
    grand_total: computed.grandTotal,
    total_taxes_and_charges: totalTaxes,
    total_commission: commissionAmount(net, { management: c.management, software: c.software, port_agent: c.port_agent, other_charges: c.other_charges }),
    quotation_markup: round2(quotation.lines.reduce((sum, l) => sum + (l.cost_unit.amount > 0 ? l.qty * l.cost_unit.amount * (c.markup / 100) : 0), 0)),
    overall_margin: poCost > 0 ? round2(((net - poCost) / poCost) * 100) : 0,
    base_total: round2(net / rate),
    base_net_total: round2(netTotal / rate),
    base_grand_total: round2(computed.grandTotal / rate),
    base_total_taxes_and_charges: round2(totalTaxes / rate),
  }
  if (quotation.taxes.length > 0) {
    // Row totals run from `net_total` (after the discount), so the last one IS the grand total.
    let running = netTotal
    out.taxes = computed.taxes.map((row, i) => {
      running = round2(running + row.tax_amount)
      return {
        charge_type: row.charge_type,
        account_head: row.account_head ?? '',
        tax_rate: row.tax_rate ?? 0,
        tax_amount: row.tax_amount,
        tax_amount_after_discount_amount: row.tax_amount,
        row_id: row.row_id || String(i + 1),
        total: running,
      }
    })
  }
  return out
}

const differs = (a: number, b: number) => Math.abs((a || 0) - (b || 0)) >= 0.005

/** Whether the stored document disagrees with the computed figures anywhere, to the cent. */
export function derivedFiguresStale(quotation: Quotation, figures: DerivedFigures): boolean {
  if (differs(quotation.discount.amount, figures.additional_discount_amount)) return true
  if (differs(quotation.base_discount_amount, figures.base_discount_amount)) return true
  if (differs(quotation.total_po_cost, figures.total_po_cost)) return true
  const st = quotation.stored_totals
  for (const key of ['net_total', 'total', 'grand_total', 'total_commission', 'quotation_markup', 'overall_margin', 'total_taxes_and_charges', 'base_total', 'base_net_total', 'base_grand_total', 'base_total_taxes_and_charges'] as const) {
    if (differs(st[key], figures[key])) return true
  }
  if (figures.taxes) {
    if (figures.taxes.length !== quotation.taxes.length) return true
    for (let i = 0; i < figures.taxes.length; i += 1) {
      if (differs(quotation.taxes[i].tax_amount, figures.taxes[i].tax_amount)) return true
      if (differs(quotation.taxes[i].total, figures.taxes[i].total)) return true
    }
  }
  return false
}
